import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useApp, useActivities } from "../context";
import {
  Window,
  Filters,
  DemoBanner,
  Loading,
  ErrorState,
} from "../components/UI";
import {
  summarize,
  distance,
  duration,
  sport,
  sportLabel,
  routePoints,
} from "../utils/activities";
export default function YourWrapped() {
  const { profile, year, units, demo } = useApp();
  const { data, loading, error, progress, retry } = useActivities();
  const [type, setType] = useState("all");
  const types = [...new Set(data.map(sport))].sort();
  const selectedType = types.includes(type) ? type : "all";
  const filtered =
    selectedType === "all"
      ? data
      : data.filter((a) => sport(a) === selectedType);
  const stats = summarize(filtered);
  const maxMonth = Math.max(...stats.months.map((m) => m.distance), 1);
  const busiest = stats.months.reduce(
    (a, b) => (b.distance > a.distance ? b : a),
    stats.months[0],
  );
  const routes = filtered.filter((a) => routePoints(a)).slice(0, 6);
  return (
    <div className="page dashboard">
      <DemoBanner />
      <div className="page-heading">
        <div>
          <p className="eyebrow">
            {year === new Date().getFullYear()
              ? "THE STORY SO FAR"
              : "THE YEAR IN REVIEW"}{" "}
            / {year}
          </p>
          <h1>
            {profile.firstname}, you
            <br />
            <em>made moves.</em>
          </h1>
          <p>Every activity added up to something. Here’s your year.</p>
        </div>
        <button className="button no-print" onClick={() => window.print()}>
          Save recap / PDF ↗
        </button>
      </div>
      <Filters />
      <div className="sport-tabs" aria-label="Activity filter">
        {["all", ...types].map((t) => (
          <button
            key={t}
            aria-pressed={selectedType === t}
            onClick={() => setType(t)}
          >
            {t === "all" ? "All activities" : sportLabel(t)}
          </button>
        ))}
      </div>
      {loading ? (
        <Loading progress={progress} />
      ) : error ? (
        <ErrorState error={error} retry={retry} />
      ) : filtered.length === 0 ? (
        <div className="empty-state">
          <h2>A blank page, for now.</h2>
          <p>
            No activities were found for {year}. Try another year or record your
            first activity on Strava.
          </p>
          <button className="button" onClick={retry}>
            Check again ↻
          </button>
        </div>
      ) : (
        <>
          <section className="stats-grid" aria-label="Year totals">
            <div className="stat-tile accent">
              <span>TOTAL DISTANCE ↗</span>
              <strong>
                {distance(stats.meters, units)}
                <small>{units}</small>
              </strong>
              <p>Every little bit went a long way.</p>
            </div>
            <div className="stat-tile">
              <span>ACTIVITIES</span>
              <strong>{filtered.length}</strong>
              <p>{stats.activeDays} days you showed up.</p>
            </div>
            <div className="stat-tile">
              <span>MOVING TIME</span>
              <strong className="time-value">{duration(stats.seconds)}</strong>
              <p>Time well spent.</p>
            </div>
            <div className="stat-tile">
              <span>ELEVATION GAIN</span>
              <strong>
                {Math.round(
                  stats.elevation * (units === "mi" ? 3.28084 : 1),
                ).toLocaleString()}
                <small>{units === "mi" ? "ft" : "m"}</small>
              </strong>
              <p>The view was worth it.</p>
            </div>
          </section>
          <div className="dashboard-grid">
            <Window title="your_rhythm / monthly distance">
              <div className="panel-content">
                <div className="panel-heading">
                  <div>
                    <p className="eyebrow">ONE MONTH AT A TIME</p>
                    <h2>Your rhythm.</h2>
                  </div>
                  <span className="tag">DISTANCE / {units.toUpperCase()}</span>
                </div>
                <div
                  className="bar-chart"
                  role="img"
                  aria-label={`Monthly distance. ${stats.months.map((m) => `${m.label}: ${distance(m.distance, units)} ${units}`).join(", ")}`}
                >
                  <div className="chart-grid" />
                  {stats.months.map((m) => (
                    <div className="chart-column" key={m.label}>
                      <span className="bar-value">
                        {distance(m.distance, units)}
                      </span>
                      <div
                        className={`bar ${m === busiest ? "highlight" : ""}`}
                        style={{ height: `${(m.distance / maxMonth) * 150}px` }}
                      />
                      <span className="bar-label">{m.label}</span>
                    </div>
                  ))}
                </div>
                <p className="chart-caption">
                  <span className="status-dot" />{" "}
                  {busiest.distance > 0
                    ? `${busiest.label} took the lead with ${distance(busiest.distance, units)} ${units}.`
                    : "These activities don’t have recorded distance."}
                </p>
                <details className="chart-table">
                  <summary>View monthly numbers</summary>
                  <table>
                    <caption>Distance and activity count by month</caption>
                    <thead>
                      <tr>
                        <th>Month</th>
                        <th>Distance ({units})</th>
                        <th>Activities</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.months.map((m) => (
                        <tr key={m.label}>
                          <th>{m.label}</th>
                          <td>{distance(m.distance, units)}</td>
                          <td>{m.count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </details>
              </div>
            </Window>
            <Window title="little_victories.txt">
              <div className="panel-content">
                <p className="eyebrow">THINGS WORTH REMEMBERING</p>
                <h2>The good stuff.</h2>
                <div className="victory-list">
                  <div>
                    <span>♥ A little encouragement</span>
                    <b>{stats.kudos.toLocaleString()} kudos</b>
                  </div>
                  <div>
                    <span>↗ New personal records</span>
                    <b>{stats.prs} PRs</b>
                  </div>
                  <div>
                    <span>☀ Days in motion</span>
                    <b>{stats.activeDays} days</b>
                  </div>
                  <div>
                    <span>↔ Better together</span>
                    <b>{stats.group} group activities</b>
                  </div>
                  {stats.averageHr && (
                    <div>
                      <span>Average heart rate</span>
                      <b>{stats.averageHr} bpm</b>
                    </div>
                  )}
                  {stats.maxHr > 0 && (
                    <div>
                      <span>Peak heart rate</span>
                      <b>{stats.maxHr} bpm</b>
                    </div>
                  )}
                </div>
                <p className="fine-print">
                  Based on the activities in this view. Heart-rate stats appear
                  when Strava provides them.
                </p>
              </div>
            </Window>
          </div>
          <section className="longest-card">
            <div className="longest-route">
              <svg viewBox="0 0 200 200" aria-hidden="true">
                <polyline
                  points={routePoints(stats.longest)}
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3"
                />
              </svg>
            </div>
            <div>
              <p className="eyebrow">THE LONG WAY ROUND</p>
              <h2>{stats.longest.name}</h2>
              <p>
                Your longest{" "}
                {selectedType === "all"
                  ? "activity"
                  : sportLabel(selectedType).toLowerCase()}{" "}
                · {distance(stats.longest.distance, units)} {units} ·{" "}
                {duration(stats.longest.moving_time)}
              </p>
              {!demo && (
                <a
                  href={`https://www.strava.com/activities/${stats.longest.id}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  View on Strava ↗
                </a>
              )}
            </div>
            <span className="record-stamp">
              PERSONAL
              <br />
              HIGHLIGHT
            </span>
          </section>
          <Window title="your_routes / a different perspective">
            <div className="route-preview-content">
              <div>
                <p className="eyebrow">LOOK WHAT YOU MADE</p>
                <h2>
                  Your miles.
                  <br />
                  Your masterpiece.
                </h2>
                <p>
                  Choose your favorite routes and make a poster you’ll want to
                  keep.
                </p>
                <Link className="button primary" to="/yourart">
                  Make route art ↗
                </Link>
              </div>
              <div className="mini-routes">
                {routes.map((a) => (
                  <svg key={a.id} viewBox="0 0 200 200" aria-hidden="true">
                    <polyline
                      points={routePoints(a)}
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3"
                    />
                  </svg>
                ))}
              </div>
            </div>
          </Window>
          <p className="data-note">
            {demo
              ? "Sample activities for illustration."
              : "Activity data from Strava. Private activities are included only if you approved that permission."}{" "}
            Year boundaries use each activity’s local date.
          </p>
        </>
      )}
    </div>
  );
}
